import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Input,
  Button,
  Layout,
  Popover,
  Select,
  Table,
  Skeleton,
  Pagination,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { FaSearch, FaFilter, FaEye, FaEdit, FaArrowLeft } from "react-icons/fa";
import { Get } from "../../config/api/get";
import { ASSETS } from "../../config/constants/api";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const ASSET_TYPES = [
  { value: "IMAGE", label: "IMAGE" },
  { value: "VIDEO", label: "VIDEO" },
  { value: "PDF", label: "PDF" },
];

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "ACTIVE" },
  { value: "INACTIVE", label: "INACTIVE" },
];

function AssetManagement() {
  const token = useSelector((state) => state.user.userToken);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: 10,
    totalDocs: 0,
    totalPages: 0,
  });
  const [filter, setFilter] = useState({
    keyword: "",
    type: undefined,
    status: undefined,
  });

  const startIndex = (paginationConfig.pageNumber - 1) * paginationConfig.limit + 1;
  const endIndex = Math.min(
    startIndex + paginationConfig.limit - 1,
    paginationConfig.totalDocs
  );
  const messageRecord = `Showing records ${endIndex} of ${paginationConfig.totalDocs}`;

  const fetchAssets = async (page, limit, keyword, type, status) => {
    setLoading(true);
    try {
      const queryObj = {
        page: String(page || paginationConfig.pageNumber),
        limit: String(limit || paginationConfig.limit),
      };
      if (keyword) queryObj.keyword = keyword;
      if (type) queryObj.type = type;
      if (status) queryObj.status = status;

      const res = await Get(ASSETS.getAssets, token, queryObj);
      const data = res?.data ?? res;
      if (data?.docs) {
        setAssets(data.docs);
        setPaginationConfig({
          pageNumber: data.page ?? page ?? 1,
          limit: data.limit ?? limit ?? 10,
          totalDocs: data.totalDocs ?? 0,
          totalPages: data.totalPages ?? 0,
        });
      } else {
        message.error(data?.message || "Failed to load assets");
      }
    } catch (e) {
      message.error(e?.message || "Failed to load assets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets(
      paginationConfig.pageNumber,
      paginationConfig.limit,
      filter.keyword || undefined,
      filter.type,
      filter.status
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page) => {
    setPaginationConfig((prev) => ({ ...prev, pageNumber: page }));
    fetchAssets(
      page,
      paginationConfig.limit,
      filter.keyword || undefined,
      filter.type,
      filter.status
    );
  };

  const handleLimitChange = (limit) => {
    setPaginationConfig((prev) => ({ ...prev, limit, pageNumber: 1 }));
    fetchAssets(1, limit, filter.keyword || undefined, filter.type, filter.status);
  };

  const handleSearch = () => {
    setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
    fetchAssets(
      1,
      paginationConfig.limit,
      filter.keyword || undefined,
      filter.type,
      filter.status
    );
  };

  const handleFilterApply = () => {
    setFilterOpen(false);
    setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
    fetchAssets(
      1,
      paginationConfig.limit,
      filter.keyword || undefined,
      filter.type,
      filter.status
    );
  };

  const resetFilter = () => {
    setFilter({ keyword: "", type: undefined, status: undefined });
    setFilterOpen(false);
    setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
    fetchAssets(1, paginationConfig.limit);
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 70,
      render: (_, __, index) =>
        (paginationConfig.pageNumber - 1) * paginationConfig.limit + index + 1,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (type) => type || "—",
    },
    {
      title: "Courses",
      key: "courses",
      ellipsis: true,
      render: (_, record) => {
        const courses = record?.courses || [];
        if (!Array.isArray(courses) || courses.length === 0) return "—";
        return courses.map((c) => (typeof c === "object" ? c?.title : c)).filter(Boolean).join(", ") || "—";
      },
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <span style={{ textTransform: "uppercase" }}>{status ?? "—"}</span>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => (date ? dayjs(date).format("M/D/YYYY") : "—"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      fixed: "right",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 4, flexWrap: "nowrap" }}>
          <Button
            type="link"
            size="small"
            icon={<FaEye />}
            onClick={() => navigate(`/asset-management/${record._id}`, { state: { asset: record } })}
          >
            View
          </Button>
          <Button
            type="link"
            size="small"
            icon={<FaEdit />}
            onClick={() => navigate(`/asset-management/${record._id}/edit`, { state: { asset: record } })}
          >
            Edit
          </Button>
        </div>
      ),
    },
  ];

  const filterContent = (
    <div className="filterDropdown">
      <div>
        <p className="mainLabel" style={{ padding: "10px" }}>Filter</p>
      </div>
      <hr style={{ margin: 0 }} />
      <div className="filterDropdownBody">
        <p className="mainLabel">Type</p>
        <Select
          size="large"
          className="filterSelectBox"
          placeholder="Select Type"
          value={filter.type}
          onChange={(v) => setFilter((f) => ({ ...f, type: v }))}
          style={{ width: "100%", marginBottom: 10 }}
          allowClear
          options={ASSET_TYPES}
        />
        <p className="mainLabel">Status</p>
        <Select
          size="large"
          className="filterSelectBox"
          placeholder="Select Status"
          value={filter.status}
          onChange={(v) => setFilter((f) => ({ ...f, status: v }))}
          style={{ width: "100%", marginBottom: 10 }}
          allowClear
          options={STATUS_OPTIONS}
        />
        <Button type="primary" shape="round" block size="large" style={{ marginBottom: 10 }} onClick={handleFilterApply}>
          Apply
        </Button>
        <Button type="default" shape="round" block size="large" onClick={resetFilter}>
          Clear All
        </Button>
      </div>
    </div>
  );

  const itemRender = (_, type, originalElement) => {
    if (type === "prev") return <button type="button" style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0 }}>Previous</button>;
    if (type === "next") return <button type="button" style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0 }}>Next</button>;
    return originalElement;
  };

  return (
    <Layout className="configuration" style={{ minHeight: "100vh" }}>
      <Row
        className="blueBackground-old coursebnr"
        style={{
          height: "45vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <FaArrowLeft
          style={{
            fontSize: "20px",
            color: "#000",
            position: "absolute",
            left: 150,
            cursor: "pointer",
            marginTop: 20,
          }}
          onClick={() => navigate(-1)}
        />
        <Typography.Title
          className="fontFamily1 aboutbanner-content"
          style={{
            fontSize: "30px",
            color: "#ffffff",
            marginTop: 20,
            marginBottom: 20,
          }}
        >
          Resource Library
        </Typography.Title>
      </Row>

      <div className="boxDetails">
        <Row
          className="whiteBackground"
          style={{ backgroundColor: "white", justifyContent: "center" }}
        >
          <Col xs={24} md={22}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                padding: "100px 20px",
              }}
            >
              <div
                className="infoBox"
                style={{ width: "100%", padding: "20px" }}
              >
                <Row style={{ padding: "10px 20px" }}>
                  <Col xs={24} md={12} style={{ display: "flex", alignItems: "center" }}>
                    <Typography.Title
                      className="fontFamily1"
                      style={{
                        fontSize: "25px",
                        fontWeight: "bold",
                        color: "black",
                        textAlign: "left",
                        marginTop: 0,
                      }}
                    >
                      Resource Library
                    </Typography.Title>
                  </Col>
                  <Col
                    xs={24}
                    md={12}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
                  >
                    <Button
                      type="primary"
                      shape="round"
                      size="large"
                      style={{ padding: "8px 40px", height: "auto" }}
                      className="loginButton"
                      onClick={() => navigate("/asset-management/add")}
                    >
                      Add Asset
                    </Button>
                  </Col>
                </Row>

                <Row style={{ padding: "10px 20px" }}>
                  <Col xs={24} md={12}>
                    <h5 style={{ display: "inline", fontSize: 16 }}>Show : </h5>
                    <Select
                      size="large"
                      className="chartSelectBox"
                      value={paginationConfig.limit}
                      onChange={handleLimitChange}
                      style={{ width: 70, textAlign: "left" }}
                      options={[
                        { value: 10, label: "10" },
                        { value: 20, label: "20" },
                        { value: 30, label: "30" },
                        { value: 50, label: "50" },
                      ]}
                    />
                    &emsp;
                    <h5 style={{ display: "inline", fontSize: 16 }}>Entries</h5>
                  </Col>
                  <Col
                    xs={24}
                    md={12}
                    style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}
                  >
                    <Popover
                      content={filterContent}
                      trigger="click"
                      open={filterOpen}
                      onOpenChange={setFilterOpen}
                      placement="bottomRight"
                      arrow={false}
                    >
                      <Button
                        style={{
                          padding: "8px 11px",
                          height: "auto",
                          borderRadius: "50px",
                          backgroundColor: "#00a79d",
                        }}
                      >
                        <FaFilter style={{ fontSize: "16px", color: "white" }} />
                      </Button>
                    </Popover>
                    &emsp;
                    <Input
                      style={{ width: 250 }}
                      className="mainInput dashInput"
                      placeholder="Search by title"
                      value={filter.keyword}
                      onChange={(e) => setFilter((f) => ({ ...f, keyword: e.target.value }))}
                      onPressEnter={handleSearch}
                      suffix={
                        <FaSearch
                          style={{ color: "#2484c6", fontSize: 16, cursor: "pointer" }}
                          onClick={handleSearch}
                        />
                      }
                    />
                  </Col>
                </Row>

                <Row style={{ padding: 20, overflow: "auto" }}>
                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Skeleton active />
                      <br />
                    </div>
                  ) : (
                    <Table
                      className="styledTable"
                      dataSource={assets}
                      columns={columns}
                      rowKey="_id"
                      pagination={false}
                      scroll={{ x: "max-content" }}
                    />
                  )}
                </Row>

                <Row style={{ padding: "10px 20px" }}>
                  <Col xs={24} md={12}>
                    <p>{messageRecord}</p>
                  </Col>
                  <Col xs={24} md={12} style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Pagination
                      className="styledPagination"
                      onChange={handlePageChange}
                      current={paginationConfig.pageNumber}
                      pageSize={paginationConfig.limit}
                      total={paginationConfig.totalDocs}
                      itemRender={itemRender}
                    />
                  </Col>
                </Row>
                <br />
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </Layout>
  );
}

export default AssetManagement;
